/**
 * Service-level Autoscaling tab.
 *
 *   - SLO defaults (the SloSet inherited by every pool that doesn't
 *     set `slo_override`)
 *   - Up / Down dynamics defaults
 *   - Schedules (kept here as an inline section — the standalone
 *     "Schedules" tab has been folded in so the SLO loop, schedule
 *     pins, and manual pins all live in one operator-facing surface)
 *
 * There used to be a "metric source" picker here that switched the
 * whole SloSet between Prometheus and Envoy. It was removed because
 * each SLO `kind` already has a single sensible backend — the
 * autoscaler picks per-row, so an operator can mix Envoy-backed
 * presets (`rps_per_pod`, error rate, latency, pending requests,
 * in-flight saturation) with `custom` PromQL SLOs under the same
 * service.
 *
 * There also used to be a standalone "Custom Metrics" section that
 * managed `CustomMetric` rows (operator-defined PromQL + threshold +
 * weight_fn) independently of the SLO loop. The UX problem was that
 * operators had two different places to "type a PromQL expression
 * with a threshold" and had to internalise that one fed the
 * autoscaler stabilization gate (`SLO kind=custom`) while the other
 * fed the routing weight-decay function (`CustomMetric`). The two
 * surfaces have been folded into the SLO loop's `custom` PromQL
 * row — see {@link SLOEditor} — which now hosts the "discover
 * metrics" button + per-row metric-name autocomplete that used to
 * live in the Custom Metrics section. The `CustomMetric` table and
 * `/services/{id}/custom-metrics` routes still exist on the
 * controller for backwards compatibility with the routing
 * weight-decay path; the WebUI no longer surfaces a way to mutate
 * them.
 *
 * The autoscaler reads these on every tick (see
 * `controller/controller/autoscaler/loop.py` →
 * `_resolve_dynamics` / `_resolve_slo_set`). A pool with overrides
 * shadows the corresponding service-level value entirely; the
 * Pool Autoscaling tab calls these "service defaults" and exposes an
 * Inherit toggle.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Gauge, Plus, Save, Trash2 } from "lucide-react";

import { api } from "@/api/client";
import type {
  Dynamics,
  Pool,
  Schedule,
  Service,
  SloSet,
} from "@/api/types";
import { CronBuilder, describeCron } from "@/components/CronBuilder";
import {
  DEFAULT_DOWN_DYNAMICS,
  DEFAULT_UP_DYNAMICS,
  DynamicsEditor,
} from "@/components/DynamicsEditor";
import { Pill } from "@/components/Pill";
import { Section } from "@/components/Section";
import { SLOEditor } from "@/components/SLOEditor";

interface Props {
  service: Service;
  pools: Pool[];
}

interface AutoscalerStateResponse {
  service_id: number;
  demand_scu: number;
  derived_demand_scu: number;
  slo_state: "violating" | "healthy" | "unknown";
  violating_since_monotonic: number | null;
  healthy_since_monotonic: number | null;
  last_scale_up_monotonic: number | null;
  last_scale_down_monotonic: number | null;
  pools: Array<{
    pool_id: number;
    priority: number;
    capacity_per_replica: number;
    min_replicas: number;
    max_replicas: number;
    desired_count: number;
    effective_scu: number;
    skipped_for_attention: boolean;
    ramp_active: boolean;
  }>;
}

/**
 * Read-only snapshot of the service-level autoscaler state (demand SCU
 * + per-pool allocator output). Hits `GET /services/:id/autoscaler-state`
 * every 10s while this tab is mounted.
 */
function ServiceAutoscalerSnapshot({
  serviceId,
  pools,
}: {
  serviceId: number;
  pools: Pool[];
}) {
  const q = useQuery({
    queryKey: ["service", serviceId, "autoscaler-state"],
    queryFn: () =>
      api.get<AutoscalerStateResponse>(
        `/services/${serviceId}/autoscaler-state`,
      ),
    refetchInterval: 10000,
  });
  const poolNameById = useMemo(() => {
    const out: Record<number, string> = {};
    for (const p of pools) out[p.id] = p.sky_service_name;
    return out;
  }, [pools]);
  const state = q.data;
  return (
    <Section
      title="Service Demand"
      icon={<Gauge />}
      count={
        state ? (
          <Pill
            tone={
              state.slo_state === "violating"
                ? "warn"
                : state.slo_state === "healthy"
                  ? "ok"
                  : "neutral"
            }
          >
            {state.slo_state}
          </Pill>
        ) : (
          "—"
        )
      }
    >
      {!state && (
        <p className="muted" style={{ fontSize: 12, margin: 0 }}>
          loading autoscaler state…
        </p>
      )}
      {state && (
        <>
          <div
            style={{
              display: "flex",
              gap: 24,
              alignItems: "baseline",
              flexWrap: "wrap",
              marginBottom: 12,
            }}
          >
            <div>
              <div className="muted" style={{ fontSize: 11 }}>
                demand_scu
              </div>
              <div className="mono" style={{ fontSize: 18 }}>
                {state.demand_scu.toFixed(2)}
              </div>
            </div>
            <div>
              <div className="muted" style={{ fontSize: 11 }}>
                derived (from desired)
              </div>
              <div className="mono" style={{ fontSize: 14 }}>
                {state.derived_demand_scu.toFixed(2)}
              </div>
            </div>
          </div>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Pool</th>
                  <th style={{ textAlign: "right" }}>Priority</th>
                  <th style={{ textAlign: "right" }}>Capacity</th>
                  <th style={{ textAlign: "right" }}>Desired</th>
                  <th style={{ textAlign: "right" }}>Effective SCU</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {state.pools.map((p) => (
                  <tr key={p.pool_id}>
                    <td className="mono">
                      {poolNameById[p.pool_id] ?? `#${p.pool_id}`}
                    </td>
                    <td className="num">{p.priority}</td>
                    <td className="num">{p.capacity_per_replica.toFixed(2)}</td>
                    <td className="num">{p.desired_count}</td>
                    <td className="num">{p.effective_scu.toFixed(2)}</td>
                    <td>
                      {p.skipped_for_attention ? (
                        <Pill tone="warn">attention skip</Pill>
                      ) : p.ramp_active ? (
                        <Pill tone="info">ramping</Pill>
                      ) : (
                        <Pill tone="neutral">active</Pill>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Section>
  );
}

const EMPTY_SLO_SET: SloSet = { items: [], aggregator: "any" };

export function ServiceAutoscaling({ service, pools }: Props) {
  const queryClient = useQueryClient();

  const [enabled, setEnabled] = useState(service.slo_default != null);
  const [sloSet, setSloSet] = useState<SloSet>(
    service.slo_default ?? EMPTY_SLO_SET,
  );
  const [up, setUp] = useState<Dynamics>(
    service.up_dynamics ?? DEFAULT_UP_DYNAMICS,
  );
  const [down, setDown] = useState<Dynamics>(
    service.down_dynamics ?? DEFAULT_DOWN_DYNAMICS,
  );

  useEffect(() => {
    setEnabled(service.slo_default != null);
    setSloSet(service.slo_default ?? EMPTY_SLO_SET);
    setUp(service.up_dynamics ?? DEFAULT_UP_DYNAMICS);
    setDown(service.down_dynamics ?? DEFAULT_DOWN_DYNAMICS);
  }, [service.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: () =>
      api.patch<Service>(`/services/${service.id}`, {
        slo_default: enabled ? sloSet : null,
        up_dynamics: enabled ? up : null,
        down_dynamics: enabled ? down : null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
    },
  });

  // After the δ redesign, pool-level SLO / dynamics overrides are no
  // longer read by the controller. We still surface them so existing
  // pool rows are visible to operators, but they're decorative — the
  // service-level row below is the only one the autoscaler consumes.
  const legacyOverridingPools = useMemo(
    () =>
      pools.filter(
        (p) =>
          p.slo_override != null ||
          p.up_dynamics_override != null ||
          p.down_dynamics_override != null,
      ),
    [pools],
  );

  return (
    <>
      {service.kind === "bank" && (
        <div className="flash" style={{ marginBottom: 12 }}>
          <strong>SLO autoscaling does not apply to the capacity bank.</strong>{" "}
          A bank has no traffic of its own — the autoscaler skips it
          entirely. How many pods it stockpiles is set by{" "}
          <code>min_replicas</code> on its pool (Pool › Autoscaling), and
          how many of them each lessee holds is set per bank link on the
          Bank page (or by the lessee's own autoscaler through that link).
        </div>
      )}
      <ServiceAutoscalerSnapshot serviceId={service.id} pools={pools} />
      <Section
        title="Service SLO"
        icon={<Gauge />}
        count={
          <Pill tone={enabled ? "ok" : "neutral"}>
            {enabled ? "active" : "fallback (legacy QPS)"}
          </Pill>
        }
        actions={
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            <Save />
            save
          </button>
        }
      >
        <div
          style={{
            display: "flex",
            gap: 16,
            alignItems: "flex-start",
            flexWrap: "wrap",
            marginBottom: 16,
          }}
        >
          <label
            style={{
              display: "inline-flex",
              gap: 8,
              alignItems: "center",
              fontSize: 12,
            }}
          >
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            Enable SLO-driven autoscaling for this service
          </label>
          <span style={{ flex: 1 }} />
          <span className="muted" style={{ fontSize: 11 }}>
            metric source: per SLO kind · Envoy presets = admin{" "}
            <code>/stats</code> · <code>custom</code> = Prometheus PromQL
          </span>
        </div>

        {!enabled && (
          <div className="flash" style={{ marginBottom: 12 }}>
            With no SLOs configured, the autoscaler falls back to the
            legacy heuristic (~1 replica per 10 RPS, capped). Pools that
            don't define their own override inherit this behaviour.
          </div>
        )}

        <div
          style={{
            opacity: enabled ? 1 : 0.55,
            pointerEvents: enabled ? undefined : "none",
          }}
        >
          <SLOEditor
            value={sloSet}
            onChange={setSloSet}
            serviceId={service.id}
          />
          <div className="split-2" style={{ marginTop: 16 }}>
            <DynamicsEditor direction="up" value={up} onChange={setUp} />
            <DynamicsEditor direction="down" value={down} onChange={setDown} />
          </div>
        </div>

        {save.isError && (
          <div className="flash" style={{ marginTop: 12 }}>
            failed to save:{" "}
            <span className="mono">{(save.error as Error).message}</span>
          </div>
        )}

        {legacyOverridingPools.length > 0 && (
          <div
            className="flash flash--warn"
            style={{ marginTop: 16, fontSize: 12 }}
          >
            <strong>{legacyOverridingPools.length}</strong> of {pools.length}{" "}
            pool
            {pools.length === 1 ? "" : "s"} still carry legacy pool-level
            SLO / dynamics overrides (
            {legacyOverridingPools.map((p, i) => (
              <span key={p.id}>
                {i > 0 && ", "}
                <code>{p.sky_service_name}</code>
              </span>
            ))}
            ). These are <strong>ignored</strong> by the controller — the
            service-level row above is the single source of truth. Clear
            them via the Pool API to remove this banner.
          </div>
        )}
      </Section>

      <ServiceSchedulesSection service={service} />
    </>
  );
}

/**
 * Schedules section (was a standalone tab before the Autoscaling tab
 * was introduced). Kept as a sub-section so the SLO loop, schedule
 * pins, and manual pins all live in one place — operators reason
 * about precedence as `manual > schedule > autoscaler` and the UI
 * groups them accordingly.
 */
function ServiceSchedulesSection({ service }: { service: Service }) {
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: ["schedules"],
    queryFn: () => api.get<Schedule[]>("/schedules"),
  });
  const filtered = useMemo(
    () =>
      (list.data ?? []).filter(
        (s) => s.scope === "service" && s.target_id === service.id,
      ),
    [list.data, service.id],
  );

  const [cron, setCron] = useState("0 9 * * 1,2,3,4,5");
  const [minR, setMinR] = useState<number | undefined>(undefined);
  const [maxR, setMaxR] = useState<number | undefined>(undefined);
  const [note, setNote] = useState("");

  const create = useMutation({
    mutationFn: () =>
      api.post<Schedule>("/schedules", {
        scope: "service",
        target_id: service.id,
        cron_expr: cron,
        min_replicas: minR,
        max_replicas: maxR,
        note: note || undefined,
        enabled: true,
        priority: 100,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["schedules"] }),
  });
  const del = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/schedules/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["schedules"] }),
  });

  return (
    <Section
      title="Schedules"
      icon={<CalendarClock />}
      count={`${filtered.length}`}
    >
      <p className="muted" style={{ fontSize: 12, margin: "0 0 12px" }}>
        Schedules overlay min/max replica bounds on a pool while their
        cron window is active. Under the δ design the autoscaler is the
        sole writer of <code>replicas_desired</code> — schedules shift
        the bounds, not the desired count. Service-scoped schedules
        listed here are kept for reference; per-pool schedules belong on
        each Pool&rsquo;s Autoscaling tab.
      </p>

      <div className="form-grid">
        <label>when</label>
        <div className="field">
          <CronBuilder value={cron} onChange={setCron} />
        </div>
        <label>min_replicas</label>
        <div className="field">
          <input
            type="number"
            min={0}
            value={minR ?? ""}
            onChange={(e) =>
              setMinR(
                e.target.value === "" ? undefined : Number(e.target.value),
              )
            }
            placeholder="leave empty to not pin a floor"
          />
          <div className="field-hint">
            Pins desired replicas at &ge; this floor while the schedule is
            active. Useful to keep a reservation hot.
          </div>
        </div>
        <label>max_replicas</label>
        <div className="field">
          <input
            type="number"
            min={0}
            value={maxR ?? ""}
            onChange={(e) =>
              setMaxR(
                e.target.value === "" ? undefined : Number(e.target.value),
              )
            }
            placeholder="optional ceiling"
          />
        </div>
        <label>note</label>
        <div className="field">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. 'EU business hours warmup'"
          />
        </div>
      </div>
      <div className="toolbar" style={{ marginTop: 12 }}>
        <button className="btn btn--primary" onClick={() => create.mutate()}>
          <Plus />
          add schedule
        </button>
      </div>

      <div className="table-wrap" style={{ marginTop: 12 }}>
        <table className="data">
          <thead>
            <tr>
              <th style={{ textAlign: "right" }}>id</th>
              <th>When</th>
              <th>Cron</th>
              <th style={{ textAlign: "right" }}>Min</th>
              <th style={{ textAlign: "right" }}>Max</th>
              <th>Enabled</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s) => (
              <tr key={s.id} className="hoverable">
                <td className="num">{s.id}</td>
                <td>{describeCron(s.cron_expr)}</td>
                <td className="mono muted">{s.cron_expr}</td>
                <td className="num">{s.min_replicas ?? "—"}</td>
                <td className="num">{s.max_replicas ?? "—"}</td>
                <td>
                  <Pill tone={s.enabled ? "ok" : "neutral"}>
                    {s.enabled ? "yes" : "no"}
                  </Pill>
                </td>
                <td className="actions">
                  <button
                    className="btn btn--danger btn--sm"
                    onClick={() => del.mutate(s.id)}
                  >
                    <Trash2 />
                    delete
                  </button>
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr>
                <td colSpan={7} className="empty">
                  no schedules for this service
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Section>
  );
}
